import { type PagyMeta, PagyPagination } from "@/components/pagy-pagination"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import AppLayout from "@/layouts/AppLayout"
import type { SelectOption } from "@/lib/options"
import { capitalize, formatDate } from "@/lib/utils"
import { Head, router, usePage } from "@inertiajs/react"
import { IconCheck, IconDots, IconEye, IconX } from "@tabler/icons-react"

interface TimeOffRequest {
  id: number
  user_name: string
  user_avatar_url: string | null
  request_type: string
  status: string
  start_date: string
  end_date: string
  days_requested: number
  created_at: string
}

// Keyed by status value, plus "all". The keys come from TimeOffRequest's status
// enum, so they are not spelled out here.
type StatusCounts = Record<string, number>

interface Props {
  [key: string]: unknown
  requests: TimeOffRequest[]
  pagy: PagyMeta
  status_counts: StatusCounts
  statuses: SelectOption[]
  filters: { status?: string; type?: string; user_id?: string }
}

const STATUS_VARIANTS: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  approved: "default",
  pending: "secondary",
  denied: "destructive",
  cancelled: "outline",
}

function formatShortDate(dateStr: string) {
  const date = new Date(dateStr)
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  })
}

export default function AdminTimeOffRequestsIndex() {
  const { requests, pagy, status_counts, statuses, filters } = usePage<Props>().props

  const statusFilters: { label: string; value?: string; count: number }[] = [
    { label: "All", value: undefined, count: status_counts.all ?? 0 },
    ...statuses.map((s) => ({ label: s.label, value: s.value, count: status_counts[s.value] ?? 0 })),
  ]

  function handleFilter(status?: string) {
    router.get(
      "/admin/time_off_requests",
      {
        status,
        type: filters.type || undefined,
        user_id: filters.user_id || undefined,
      },
      { preserveState: true },
    )
  }

  function handleApprove(id: number) {
    if (confirm("Approve this request? Days will be deducted from the user's balance.")) {
      router.patch(`/admin/time_off_requests/${id}/approve`)
    }
  }

  function handleDeny(id: number) {
    if (confirm("Deny this request?")) {
      router.patch(`/admin/time_off_requests/${id}/deny`)
    }
  }

  return (
    <>
      <Head title="Time Off Requests (Admin) - Operations Center" />
      <div className="flex flex-1 flex-col gap-4 p-4 lg:p-6">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/">Home</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Time Off Requests</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <h1 className="text-xl font-bold">Time Off Requests</h1>

        <div className="flex flex-wrap items-center gap-2">
          <span className="text-muted-foreground hidden text-sm sm:inline">Show:</span>
          {statusFilters.map((filter) => (
            <Button
              key={filter.label}
              variant={filters.status === filter.value ? "default" : "outline"}
              size="sm"
              onClick={() => handleFilter(filter.value)}
            >
              {filter.label} ({filter.count})
            </Button>
          ))}
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>User</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Dates</TableHead>
              <TableHead className="text-right">Days</TableHead>
              <TableHead>Submitted</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-10">
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {requests.map((request) => (
              <TableRow key={request.id}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Avatar size="sm">
                      {request.user_avatar_url ? (
                        <AvatarImage src={request.user_avatar_url} alt={request.user_name} />
                      ) : null}
                      <AvatarFallback>{request.user_name.charAt(0).toUpperCase()}</AvatarFallback>
                    </Avatar>
                    <span className="font-medium">{request.user_name}</span>
                  </div>
                </TableCell>
                <TableCell className="capitalize">{request.request_type}</TableCell>
                <TableCell>
                  {formatDate(request.start_date)} — {formatDate(request.end_date)}
                </TableCell>
                <TableCell className="text-right">{request.days_requested}</TableCell>
                <TableCell className="text-muted-foreground">{formatShortDate(request.created_at)}</TableCell>
                <TableCell>
                  <Badge variant={STATUS_VARIANTS[request.status] || "outline"}>{capitalize(request.status)}</Badge>
                </TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <IconDots className="h-4 w-4" />
                        <span className="sr-only">Actions</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem asChild>
                        <a href={`/admin/time_off_requests/${request.id}`}>
                          <IconEye className="mr-2 h-4 w-4" />
                          View
                        </a>
                      </DropdownMenuItem>
                      {request.status === "pending" && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => handleApprove(request.id)}>
                            <IconCheck className="mr-2 h-4 w-4" />
                            Approve
                          </DropdownMenuItem>
                          <DropdownMenuItem variant="destructive" onClick={() => handleDeny(request.id)}>
                            <IconX className="mr-2 h-4 w-4" />
                            Deny
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
            {requests.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-muted-foreground text-center">
                  No requests found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        <PagyPagination pagy={pagy} />
      </div>
    </>
  )
}

AdminTimeOffRequestsIndex.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
